<template>
  <section class="mt-8">
    <div class="w-full flex justify-between items-center">
      <h4 class="capitalize">
        {{ $t("labels.generic", { pt: "Questões", en: "Questions" }) }}
      </h4>
      <div class="md:flex items-center gap-3 relative">
        <Button
          :label="$t('labels.generic', { pt: 'Baixar', en: 'Download' })"
          icon="pi pi-download"
          class="p-button-sm btn-primary h-8"
          :loading="btnLoading"
          @click="openActionOptions()"
        />
        <div
          ref="ActionOptions"
          class="bg-white w-72 py-2 shadow-md rounded-md absolute top-12 right-0 z-50"
          v-if="showActionOptions"
        >
          <Button
            id="add-user-btn"
            class="btn-simple p-button-sm w-full gap-2 flex items-center"
            @click="downloadQuizQuestions()"
          >
            <v-icon name="md-quiz"></v-icon>
            <p class="text-[14px]">
              {{
                $t("labels.generic", {
                  pt: "Questões das avaliações",
                  en: "Quiz questions",
                })
              }}
            </p>
          </Button>
          <Button
            id="download-template-btn"
            class="btn-simple p-button-sm w-full gap-2 flex items-center"
            @click="downloadQuizQuestions()"
          >
            <v-icon name="md-textsms"></v-icon>
            <p class="text-[14px] ml-1">
              {{
                $t("labels.generic", {
                  pt: "Questões do chat",
                  en: "Chat questions",
                })
              }}
            </p>
          </Button>
        </div>
      </div>
    </div>
    <div
      class="w-full mt-8 bg-[#F8F9FA] rounded-md border-[1px] border-[#D0D0D1]"
    >
      <div class="w-full bg-white h-[60px] border-b-2 flex">
        <button
          @click="
            quizPaginated(true);
            loader = true;
          "
          :class="`h-full w-[150px] flex justify-center items-center bg-transparent border-b-2 ${
            quizAnswer
              ? ' border-h-primary-500 text-h-primary-500'
              : 'border-[#0000] text-neutral-400'
          }`"
        >
          <p class="font-[700]">
            {{ $t("labels.generic", { pt: "Avaliações", en: "Quiz" }) }}
          </p>
        </button>
        <button
          @click="
            quizPaginated(false);
            loader = true;
          "
          :class="`h-full w-[150px] flex justify-center items-center bg-transparent border-b-2 ${
            !quizAnswer
              ? ' border-h-primary-500 text-h-primary-500'
              : 'border-[#0000] text-neutral-400'
          }`"
        >
          <p class="font-[700]">Chat</p>
        </button>
      </div>
      <div class="mt-4" v-if="!loader">
        <QuizAnswer v-if="quizAnswer" :questions="questions" />
        <QuizChat v-if="!quizAnswer" :questions="questionsChat" />
      </div>
      <div v-else>
        <Loader :loader="loader" />
      </div>
      <Paginator
        v-if="!loader && params?.total"
        :rows="params?.size"
        :totalRecords="params?.total"
        @page="paginate($event)"
      ></Paginator>
    </div>
  </section>
</template>

<script lang="ts">
import { QueryParams } from "@/models/query-params.model";
import { delay, last, take } from "rxjs";
import { defineAsyncComponent, defineComponent } from "vue";
import { QuestionsService } from "./questions.service";
import {
  QuestionsChatObjectiveStats,
  QuestionsObjectiveStats,
} from "@/models/stats.model";
import { StatsRest } from "@/services/rest/stats.rest";
import saveAs from "file-saver";

const QuizAnswer = defineAsyncComponent(
  () => import("./quizAnswer/quiz-answer.vue")
);

const QuizChat = defineAsyncComponent(
  () => import("./quizAnswer/quiz-chat.vue")
);

export default defineComponent({
  name: "questionsStats",
  components: { QuizAnswer, QuizChat },
  computed: {
    service(): QuestionsService {
      return new QuestionsService();
    },
    rest(): StatsRest {
      return new StatsRest();
    },
    classroomId(): string {
      return String(localStorage.getItem("objClassroomId"));
    },
  },
  watch: {
    $route() {
      if (this.$route.name === "statsObjective") {
        this.loader = true;
        this.params.page = 0;
        this.params.size = 2;
        this.quizPaginated(true);
      }
    },
  },
  data() {
    return {
      data: [] as any,
      loader: false as boolean,
      quizAnswer: true as boolean,
      params: new QueryParams(0, 2),
      questions: [] as QuestionsObjectiveStats[],
      questionsChat: [] as QuestionsChatObjectiveStats[],
      btnLoading: false as boolean,
      showActionOptions: false as boolean,
    };
  },
  mounted() {
    this.quizPaginated(true);
  },
  methods: {
    paginate(event: { page: number; rows: number }): void {
      const params = new QueryParams(event.page, event.rows);
      this.getQuestionsQuizStats(params);
    },
    quizPaginated(value: boolean): void {
      this.quizAnswer = value;
      const page = this.params.page;
      const size = 2;
      const params = new QueryParams(page, size);
      if (this.quizAnswer) this.getQuestionsQuizStats(params);
      else this.getQuestionsChatStats(params);
    },
    getQuestionsQuizStats(queryParams: QueryParams): void {
      this.service.questions.pipe(take(2), last()).subscribe({
        next: (response: any) => {
          this.questions = response.content;
          this.params.total = response.total;
          this.loader = false;
        },
        error: () => {
          this.loader = false;
        },
      });
      this.service.getQuestionsFromObjectiveStats(
        String(this.$route.params.objectiveId),
        queryParams
      );
    },
    getQuestionsChatStats(queryParams: QueryParams): void {
      this.service.chat.pipe(take(2), last()).subscribe({
        next: (response: any) => {
          this.questionsChat = response.content;
          this.params.total = response.total;
          this.loader = false;
        },
        error: () => {
          this.loader = false;
        },
      });
      this.service.getChatQuestionsFromObjectiveStats(
        String(this.$route.params.objectiveId),
        queryParams
      );
    },
    getBackground(idItem: any, idCorrectAnswer: any): string {
      let result = "bg-h-error-400 text-black";
      if (idItem === idCorrectAnswer) {
        result = "bg-[#74B543] text-black";
      }
      return result;
    },
    downloadQuizQuestions(): void {
      this.btnLoading = true;
      this.rest
        .getQuestionsQuizStatsCsv(String(this.$route.params.objectiveId), {
          classroomId: this.classroomId,
        })
        .pipe(delay(1000))
        .subscribe({
          next: (response) => {
            this.btnLoading = false;
            const blob = new Blob([response], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            saveAs(url, `questions_quiz_data.csv`);
          },
          error: () => {
            this.btnLoading = false;
          },
        });
    },
    downloadChatQuestions(): void {
      this.btnLoading = true;
      this.rest
        .getQuestionsChatStatsCsv(String(this.$route.params.objectiveId), {
          classroomId: this.classroomId,
        })
        .pipe(delay(1000))
        .subscribe({
          next: (response) => {
            this.btnLoading = false;
            const blob = new Blob([response], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            saveAs(url, `questions_chat_data.csv`);
          },
          error: () => {
            this.btnLoading = false;
          },
        });
    },
    openActionOptions(): void {
      if (!this.showActionOptions) {
        this.showActionOptions = true;
        setTimeout(() => {
          document.addEventListener("click", this.closeActionOptions);
        }, 100);
      }
    },
    closeActionOptions(event): void {
      const myDiv = this.$refs.ActionOptions as HTMLElement;
      if (myDiv && !myDiv.contains(event.target as Node)) {
        this.showActionOptions = false;
        document.removeEventListener("click", this.closeActionOptions);
      }
    },
  },
});
</script>
